// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Einkauf header (device pass 2026-10-10, owner-approved): the week's food
 * value first, compared with the user's weekly budget when one is set; the
 * whole-pack checkout of the visible rows second.
 *
 *   "Diese Woche ≈ 57,69 € · Budget 70 €"   (or "· 12 € über Budget", or no budget part)
 *   "Einkauf in ganzen Packungen ≈ 112,08 € · Reste bleiben in deiner Küche"
 *
 * The week figure is the SAME number the budget is judged against (the live
 * verdict's goal forecast, ADR-052 / ADR-050), so header and budget never
 * disagree. It is served only when every row is priced; otherwise this returns
 * null and the screen keeps its existing "priced so far" headline — never a
 * lower bound dressed as the week's value. A legacy, unclassified limit is not a
 * budget and is never shown as one. Mirror of mobile/src/lib/weekBudgetHeadline.ts — keep the two identical.
 */
import type { BudgetVerdict } from "@bitebalance/contracts"

type T = (key: string, vars?: Record<string, string | number>) => string

/** Whole euros print without cents ("70 €", "€70"); anything else as the formatter gives it. */
function money(cents: number, fmt: (cents: number) => string): string {
  const text = fmt(cents)
  return cents % 100 === 0 ? text.replace(/([.,])00(?!\d)/, "") : text
}

export interface WeekBudgetHeadline {
  primary: string
  /** Null when the visible rows' checkout is not a complete total. */
  secondary: string | null
  overBudget: boolean
}

export function weekBudgetHeadline(
  verdict: BudgetVerdict | undefined,
  completeCheckoutCents: number | null,
  t: T,
  fmt: (cents: number) => string,
): WeekBudgetHeadline | null {
  const goal = verdict?.goal
  const week = goal?.forecastWeeklyAverageCents
  if (!goal || typeof week !== "number") return null
  const amount = fmt(week)
  let primary = t("views.shop.weekHeadline", { amount })
  let overBudget = false
  if (goal.status === "above_goal" && typeof goal.goalCents === "number" && typeof goal.deltaCents === "number") {
    primary = t("views.shop.weekHeadlineOverBudget", { amount, delta: money(goal.deltaCents, fmt) })
    overBudget = true
  } else if (goal.status === "at_or_below_goal" && typeof goal.goalCents === "number") {
    primary = t("views.shop.weekHeadlineBudget", { amount, budget: money(goal.goalCents, fmt) })
  }
  const secondary = completeCheckoutCents === null ? null : t("views.shop.weekCheckoutLine", { amount: fmt(completeCheckoutCents) })
  return { primary, secondary, overBudget }
}
